import { getSupabase, supabasePublic, supabaseSchema } from "../components/supabaseClient";
import {
  prompts,
  colid,
  getTypeFieldIndex,
  generateDocFilename,
} from "../components/env";
import { dbgLog } from "../utils/appDebugFlags";
import {
  checkFrontendStorageJsonBlobSize,
  isProbablyJsonStoragePath,
  isRestrictedJsonStorageBucket,
  preflightRestrictedStorageJsonSize,
} from "../utils/storageJsonFrontendLimit";
import { hydrateScheduleRowsTimezoneSource } from "../utils/scheduleRowTimezone.js";

const duLog = (...args) => dbgLog("document-utilities", ...args);

//This will scroll to the section title
export const scrollToSection = (editor, sectionTitle) => {
    const lines = editor.getLines();
    for (let i = 0; i < lines.length; i++) {
      const lineText = lines[i].domNode?.innerText.trim();
      if (lineText === sectionTitle) {
        const index = editor.getIndex(lines[i]);
        editor.setSelection(index, 0);
  
        const bounds = editor.getBounds(index);
        const container = editor.root.parentNode;
        const offset = 80;
  
        container.scrollTop = bounds.top + container.scrollTop - offset;
        break;
      }
    }
  };

// Scroll to a cursor position and center it in the viewport
export const scrollToCursorAndCenter = (editor, cursorIndex, cursorLength = 0) => {
  try {
    // Set the cursor position first
    editor.setSelection(cursorIndex, cursorLength);
    
    // Wait a moment for the selection to be applied and DOM to update
    setTimeout(() => {
      try {
        // Method 1: Manual centering using getBounds and container scroll (most reliable)
        const bounds = editor.getBounds(cursorIndex);
        if (bounds && editor.container) {
          const container = editor.container;
          const containerRect = container.getBoundingClientRect();
          const viewportHeight = containerRect.height;
          
          // Calculate the scroll position to center the cursor
          const targetScrollTop = bounds.top + container.scrollTop - (viewportHeight / 2) + (bounds.height / 2);
          
          // Ensure we don't scroll beyond the document bounds
          const maxScrollTop = container.scrollHeight - viewportHeight;
          const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
          
          duLog('🎯 Centering cursor:', {
            cursorIndex,
            bounds,
            viewportHeight,
            targetScrollTop,
            finalScrollTop,
            currentScrollTop: container.scrollTop
          });
          
          container.scrollTo({
            top: finalScrollTop,
            behavior: 'smooth'
          });
          duLog('✅ Scrolled to cursor position using manual centering calculation');
          return;
        }
        
        // Method 2: Try to get the leaf and scroll with centering
        const leaf = editor.getLeaf(cursorIndex);
        if (leaf && leaf[0] && leaf[0].domNode && leaf[0].domNode.scrollIntoView) {
          leaf[0].domNode.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
          duLog('✅ Scrolled to cursor position using getLeaf with centering');
          return;
        }
        
        // Method 3: Use Quill's scrollIntoView with centering (deprecated but works)
        if (editor.scrollIntoView) {
          editor.scrollIntoView(cursorIndex, { 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
          duLog('✅ Scrolled to cursor position using Quill scrollIntoView with centering');
          return;
        }
        
        // Method 4: Fallback to Quill's built-in scrolling (brings into view but doesn't center)
        if (editor.scrollSelectionIntoView) {
          editor.scrollSelectionIntoView();
          duLog('⚠️ Scrolled to cursor position using Quill scrollSelectionIntoView (fallback - not centered)');
          return;
        }
        
        // Method 5: Final fallback - scroll the editor container
        const editorContainer = editor.container;
        if (editorContainer) {
          editorContainer.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
          duLog('✅ Scrolled to cursor position using container (final fallback)');
          return;
        }
        
        duLog('📍 Setting cursor position without scrolling');
      } catch (innerError) {
        duLog('❌ Error in delayed scrolling:', innerError);
      }
    }, 50); // Small delay to ensure DOM is updated
    
    return true; // Return immediately, actual scrolling happens in setTimeout
  } catch (error) {
    duLog('❌ Error scrolling to cursor position:', error);
    duLog('📍 Setting cursor position without scrolling');
    return false;
  }
};

// Alternative robust centering method that tries multiple approaches
export const scrollToCursorAndCenterRobust = (editor, cursorIndex, cursorLength = 0) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    duLog(`🚀 [ROBUST] Starting cursor centering at ${new Date().toISOString()}`, {
      cursorIndex,
      cursorLength,
      editorExists: !!editor,
      containerExists: !!editor?.container
    });
    
    try {
      // Set the cursor position first
      editor.setSelection(cursorIndex, cursorLength);
      duLog(`📍 [ROBUST] Set selection to index ${cursorIndex}, length ${cursorLength}`);
      
      // Try multiple approaches with increasing delays to ensure DOM updates
      const attempts = [
        { delay: 0, method: 'immediate' },
        { delay: 50, method: 'short_delay' },
        { delay: 100, method: 'medium_delay' }
      ];
      
      let attemptCount = 0;
      
      const tryScroll = (delay, method) => {
        setTimeout(() => {
          const attemptStartTime = Date.now();
          try {
            duLog(`🎯 [ROBUST] Attempt ${attemptCount + 1}/3: ${method} (delay: ${delay}ms) at ${new Date().toISOString()}`);
            
            // Get fresh bounds after potential DOM updates
            let bounds = editor.getBounds(cursorIndex);
            duLog(`📏 [ROBUST] Initial bounds for cursor ${cursorIndex}:`, bounds);
            
            // If bounds are invalid or zero, try alternative methods
            if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
              duLog(`⚠️ [ROBUST] Invalid bounds, trying alternative methods for cursor ${cursorIndex}`);
              
              // Method 1: Try to get bounds from selection
              const selection = editor.getSelection();
              if (selection && selection.index === cursorIndex) {
                bounds = editor.getBounds(selection.index);
                duLog(`📏 [ROBUST] Alternative bounds from selection:`, bounds);
              }
              
                     // Method 2: Try to get the leaf and calculate bounds manually
                     if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
                       const leaf = editor.getLeaf(cursorIndex);
                       if (leaf && leaf[0] && leaf[0].domNode) {
                         let domNode = leaf[0].domNode;
                         
                         // Handle case where domNode is a Text node
                         if (domNode.nodeType === Node.TEXT_NODE) {
                           duLog(`📏 [ROBUST] Leaf is Text node, finding parent element`);
                           domNode = domNode.parentElement;
                           if (!domNode) {
                             duLog(`❌ [ROBUST] No parent element found for Text node`);
                             return;
                           }
                           duLog(`📏 [ROBUST] Using parent element:`, domNode);
                         }
                         
                         const rect = domNode.getBoundingClientRect();
                         const containerRect = editor.container.getBoundingClientRect();
                         bounds = {
                           top: rect.top - containerRect.top + editor.container.scrollTop,
                           left: rect.left - containerRect.left,
                           height: rect.height,
                           width: rect.width
                         };
                         duLog(`📏 [ROBUST] Manual bounds calculation:`, bounds);
                       }
                     }
              
              // Method 3: Try to get bounds from a slightly different index
              if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
                const alternativeBounds = editor.getBounds(cursorIndex + 1) || editor.getBounds(cursorIndex - 1);
                if (alternativeBounds && alternativeBounds.top > 0) {
                  bounds = alternativeBounds;
                  duLog(`📏 [ROBUST] Alternative bounds from nearby index:`, bounds);
                }
              }
            }
            
            if (bounds && bounds.top > 0 && bounds.height > 0 && editor.container) {
              const container = editor.container;
              const containerRect = container.getBoundingClientRect();
              const viewportHeight = containerRect.height;
              const currentScrollTop = container.scrollTop;
              const scrollHeight = container.scrollHeight;
              
              // Calculate the ideal center position
              const idealCenterScrollTop = bounds.top + currentScrollTop - (viewportHeight / 2) + (bounds.height / 2);
              
              // Ensure we don't scroll beyond the document bounds (best-effort centering)
              const maxScrollTop = scrollHeight - viewportHeight;
              const bestEffortScrollTop = Math.max(0, Math.min(idealCenterScrollTop, maxScrollTop));
              
              duLog(`🎯 [ROBUST] Centering calculation (${method}):`, {
                cursorIndex,
                bounds,
                viewportHeight,
                currentScrollTop,
                scrollHeight,
                maxScrollTop,
                idealCenterScrollTop,
                bestEffortScrollTop,
                canCenterFully: idealCenterScrollTop >= 0 && idealCenterScrollTop <= maxScrollTop,
                willScroll: Math.abs(bestEffortScrollTop - currentScrollTop) > 5,
                scrollDistance: bestEffortScrollTop - currentScrollTop,
                boundsTop: bounds.top,
                boundsHeight: bounds.height,
                cursorInViewport: bounds.top >= 0 && bounds.top <= viewportHeight
              });
              
              // Always try to scroll to center, even if it seems close
              // The previous logic was too conservative
              const scrollThreshold = 10; // Reduced from 5 to be more aggressive
              if (Math.abs(bestEffortScrollTop - currentScrollTop) < scrollThreshold) {
                duLog(`⚠️ [ROBUST] Scroll distance small (${Math.abs(bestEffortScrollTop - currentScrollTop)}px) but attempting anyway (${method})`);
                // Don't return early, continue with the scroll attempt
              }
              
              const beforeScrollTop = container.scrollTop;
              container.scrollTo({
                top: bestEffortScrollTop,
                behavior: 'smooth'
              });
              
              // Check if scroll actually happened
              setTimeout(() => {
                const afterScrollTop = container.scrollTop;
                const actualScrollDistance = afterScrollTop - beforeScrollTop;
                const expectedScrollDistance = bestEffortScrollTop - beforeScrollTop;
                const scrollAccuracy = Math.abs(actualScrollDistance - expectedScrollDistance);
                
                duLog(`📊 [ROBUST] Scroll result (${method}):`, {
                  beforeScrollTop,
                  afterScrollTop,
                  expectedScrollTop: bestEffortScrollTop,
                  actualScrollDistance,
                  expectedScrollDistance,
                  scrollAccuracy,
                  success: scrollAccuracy < 50, // Consider successful if within 50px
                  attemptDuration: Date.now() - attemptStartTime
                });
                
                if (scrollAccuracy < 50) {
                  duLog(`✅ [ROBUST] Successfully centered cursor using ${method}`);
                  resolve(true);
                } else {
                  duLog(`⚠️ [ROBUST] Scroll accuracy poor for ${method}, trying next attempt`);
                  attemptCount++;
                  if (attemptCount < attempts.length) {
                    const nextAttempt = attempts[attemptCount];
                    tryScroll(nextAttempt.delay, nextAttempt.method);
                  } else {
                    duLog('❌ [ROBUST] All attempts completed with poor accuracy');
                    resolve(false);
                  }
                }
              }, 100); // Wait for smooth scroll to complete
              
              return;
            } else {
              duLog(`❌ [ROBUST] No bounds or container available for ${method}:`, {
                bounds: !!bounds,
                container: !!editor.container,
                boundsValue: bounds
              });
            }
            
            // If bounds are not available, try alternative methods
            const leaf = editor.getLeaf(cursorIndex);
            duLog(`🍃 [ROBUST] Leaf for cursor ${cursorIndex}:`, leaf);
            
            if (leaf && leaf[0] && leaf[0].domNode && leaf[0].domNode.scrollIntoView) {
              duLog(`🍃 [ROBUST] Using getLeaf scrollIntoView for ${method}`);
              leaf[0].domNode.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'center'
              });
              duLog(`✅ [ROBUST] Scrolled using getLeaf with ${method}`);
              resolve(true);
              return;
            }
            
            // Try next attempt if this one failed
            attemptCount++;
            if (attemptCount < attempts.length) {
              duLog(`🔄 [ROBUST] Attempt ${attemptCount} failed, trying next method`);
              const nextAttempt = attempts[attemptCount];
              tryScroll(nextAttempt.delay, nextAttempt.method);
            } else {
              // All attempts failed, use fallback
              duLog(`❌ [ROBUST] All attempts failed, using fallback scrollSelectionIntoView`);
              if (editor.scrollSelectionIntoView) {
                editor.scrollSelectionIntoView();
                duLog('⚠️ [ROBUST] Used fallback scrollSelectionIntoView (not centered)');
              }
              resolve(false);
            }
            
          } catch (error) {
            duLog(`❌ [ROBUST] Error in scroll attempt ${method}:`, error);
            attemptCount++;
            if (attemptCount < attempts.length) {
              const nextAttempt = attempts[attemptCount];
              tryScroll(nextAttempt.delay, nextAttempt.method);
            } else {
              duLog(`❌ [ROBUST] All attempts failed due to errors`);
              resolve(false);
            }
          }
        }, delay);
      };
      
      // Start with the first attempt
      const firstAttempt = attempts[0];
      tryScroll(firstAttempt.delay, firstAttempt.method);
      
    } catch (error) {
      duLog('❌ [ROBUST] Error in robust scrolling:', error);
      resolve(false);
    }
  });
};

// Simple synchronous centering method as a backup
export const scrollToCursorAndCenterSimple = (editor, cursorIndex, cursorLength = 0) => {
  const startTime = Date.now();
  duLog(`🚀 [SIMPLE] Starting simple cursor centering at ${new Date().toISOString()}`, {
    cursorIndex,
    cursorLength,
    editorExists: !!editor,
    containerExists: !!editor?.container
  });
  
  try {
    // Set the cursor position first
    editor.setSelection(cursorIndex, cursorLength);
    duLog(`📍 [SIMPLE] Set selection to index ${cursorIndex}, length ${cursorLength}`);
    
    // Force a synchronous update by accessing the DOM directly
    let bounds = editor.getBounds(cursorIndex);
    duLog(`📏 [SIMPLE] Initial bounds for cursor ${cursorIndex}:`, bounds);
    
    // If bounds are invalid or zero, try alternative methods
    if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
      duLog(`⚠️ [SIMPLE] Invalid bounds, trying alternative methods for cursor ${cursorIndex}`);
      
      // Method 1: Try to get bounds from selection
      const selection = editor.getSelection();
      if (selection && selection.index === cursorIndex) {
        bounds = editor.getBounds(selection.index);
        duLog(`📏 [SIMPLE] Alternative bounds from selection:`, bounds);
      }
      
      // Method 2: Try to get the leaf and calculate bounds manually
      if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
        const leaf = editor.getLeaf(cursorIndex);
        if (leaf && leaf[0] && leaf[0].domNode) {
          let domNode = leaf[0].domNode;
          
          // Handle case where domNode is a Text node
          if (domNode.nodeType === Node.TEXT_NODE) {
            duLog(`📏 [SIMPLE] Leaf is Text node, finding parent element`);
            domNode = domNode.parentElement;
            if (!domNode) {
              duLog(`❌ [SIMPLE] No parent element found for Text node`);
              return false;
            }
            duLog(`📏 [SIMPLE] Using parent element:`, domNode);
          }
          
          const rect = domNode.getBoundingClientRect();
          const containerRect = editor.container.getBoundingClientRect();
          bounds = {
            top: rect.top - containerRect.top + editor.container.scrollTop,
            left: rect.left - containerRect.left,
            height: rect.height,
            width: rect.width
          };
          duLog(`📏 [SIMPLE] Manual bounds calculation:`, bounds);
        }
      }
      
      // Method 3: Try to get bounds from a slightly different index
      if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
        const alternativeBounds = editor.getBounds(cursorIndex + 1) || editor.getBounds(cursorIndex - 1);
        if (alternativeBounds && alternativeBounds.top > 0) {
          bounds = alternativeBounds;
          duLog(`📏 [SIMPLE] Alternative bounds from nearby index:`, bounds);
        }
      }
    }
    
    if (bounds && bounds.top > 0 && bounds.height > 0 && editor.container) {
      const container = editor.container;
      const containerRect = container.getBoundingClientRect();
      const viewportHeight = containerRect.height;
      const currentScrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      
      // Calculate the ideal center position
      const idealCenterScrollTop = bounds.top + currentScrollTop - (viewportHeight / 2) + (bounds.height / 2);
      
      // Ensure we don't scroll beyond the document bounds (best-effort centering)
      const maxScrollTop = scrollHeight - viewportHeight;
      const bestEffortScrollTop = Math.max(0, Math.min(idealCenterScrollTop, maxScrollTop));
      
      duLog(`🎯 [SIMPLE] Centering calculation:`, {
        cursorIndex,
        bounds,
        viewportHeight,
        currentScrollTop,
        scrollHeight,
        maxScrollTop,
        idealCenterScrollTop,
        bestEffortScrollTop,
        canCenterFully: idealCenterScrollTop >= 0 && idealCenterScrollTop <= maxScrollTop,
        willScroll: Math.abs(bestEffortScrollTop - currentScrollTop) > 5,
        scrollDistance: bestEffortScrollTop - currentScrollTop,
        boundsTop: bounds.top,
        boundsHeight: bounds.height,
        cursorInViewport: bounds.top >= 0 && bounds.top <= viewportHeight
      });
      
      // Always try to scroll to center, even if it seems close
      const scrollThreshold = 10; // Reduced from 5 to be more aggressive
      if (Math.abs(bestEffortScrollTop - currentScrollTop) < scrollThreshold) {
        duLog(`⚠️ [SIMPLE] Scroll distance small (${Math.abs(bestEffortScrollTop - currentScrollTop)}px) but attempting anyway`);
        // Don't return early, continue with the scroll attempt
      }
      
      // Use immediate scrolling (no smooth behavior for reliability)
      const beforeScrollTop = container.scrollTop;
      container.scrollTop = bestEffortScrollTop;
      const afterScrollTop = container.scrollTop;
      
      const actualScrollDistance = afterScrollTop - beforeScrollTop;
      const expectedScrollDistance = bestEffortScrollTop - beforeScrollTop;
      const scrollAccuracy = Math.abs(actualScrollDistance - expectedScrollDistance);
      
      duLog(`📊 [SIMPLE] Scroll result:`, {
        beforeScrollTop,
        afterScrollTop,
        expectedScrollTop: bestEffortScrollTop,
        actualScrollDistance,
        expectedScrollDistance,
        scrollAccuracy,
        success: scrollAccuracy < 5, // Simple method should be very accurate
        duration: Date.now() - startTime
      });
      
      if (scrollAccuracy < 5) {
        duLog('✅ [SIMPLE] Successfully centered cursor position');
        return true;
      } else {
        duLog('⚠️ [SIMPLE] Scroll accuracy poor, but position was set');
        return false;
      }
    } else {
      duLog(`❌ [SIMPLE] No bounds or container available:`, {
        bounds: !!bounds,
        container: !!editor.container,
        boundsValue: bounds
      });
    }
    
    return false;
  } catch (error) {
    duLog('❌ [SIMPLE] Error in simple centering:', error);
    return false;
  }
};

// Native scrollIntoView centering method (most reliable for centering)
export const scrollToCursorAndCenterNative = (editor, cursorIndex, cursorLength = 0) => {
  const startTime = Date.now();
  duLog(`[LOG-1] 🚀 [NATIVE] Starting native cursor centering at ${new Date().toISOString()}`, {
    cursorIndex,
    cursorLength,
    editorExists: !!editor,
    containerExists: !!editor?.container
  });
  
  try {
    // Set the cursor position first
    editor.setSelection(cursorIndex, cursorLength);
    duLog(`[LOG-2] 📍 [NATIVE] Set selection to index ${cursorIndex}, length ${cursorLength}`);
    
    // Try to get the leaf and use native scrollIntoView
    const leaf = editor.getLeaf(cursorIndex);
    duLog(`[LOG-3] 🍃 [NATIVE] Leaf for cursor ${cursorIndex}:`, leaf);
    
    if (leaf && leaf[0] && leaf[0].domNode) {
      let domNode = leaf[0].domNode;
      const container = editor.container;
      
      // Handle case where domNode is a Text node (doesn't have getBoundingClientRect)
      if (domNode.nodeType === Node.TEXT_NODE) {
        duLog(`🍃 [NATIVE] Leaf is Text node, finding parent element`);
        domNode = domNode.parentElement;
        if (!domNode) {
          duLog(`❌ [NATIVE] No parent element found for Text node`);
          return false;
        }
        duLog(`🍃 [NATIVE] Using parent element:`, domNode);
      }
      
      // Verify the element has getBoundingClientRect method
      if (typeof domNode.getBoundingClientRect !== 'function') {
        duLog(`❌ [NATIVE] Element doesn't have getBoundingClientRect method:`, domNode);
        return false;
      }
      
      // Get cursor position relative to viewport and document
      const containerRect = container.getBoundingClientRect();
      const domNodeRect = domNode.getBoundingClientRect();
      const viewportHeight = containerRect.height;
      
      // Calculate cursor position relative to viewport (for visibility check)
      const cursorTopInViewport = domNodeRect.top - containerRect.top;
      const cursorBottomInViewport = domNodeRect.bottom - containerRect.top;
      const isCursorVisible = cursorTopInViewport >= 0 && cursorBottomInViewport <= viewportHeight;
      
      // Calculate absolute position in document (for centering calculation)
      const absoluteTop = domNodeRect.top - containerRect.top + container.scrollTop;
      
      // Calculate the ideal center position - where to scroll so cursor is in middle of viewport
      // Formula: scrollTop = absoluteTop - (viewportHeight / 2) + (cursorHeight / 2)
      const idealCenterScrollTop = absoluteTop - (viewportHeight / 2) + (domNodeRect.height / 2);
      const maxScrollTop = container.scrollHeight - viewportHeight;
      const bestEffortScrollTop = Math.max(0, Math.min(idealCenterScrollTop, maxScrollTop));
      
      // Check if cursor is actually near the top of the document (not just viewport)
      const isNearTopOfDocument = absoluteTop < viewportHeight * 0.3;
      const isVeryTopOfDocument = absoluteTop < viewportHeight * 0.1;
      
      duLog(`🍃 [NATIVE] Cursor position analysis:`);
      duLog(`  - absoluteTop: ${absoluteTop}`);
      duLog(`  - cursorTopInViewport: ${cursorTopInViewport}`);
      duLog(`  - cursorBottomInViewport: ${cursorBottomInViewport}`);
      duLog(`  - isCursorVisible: ${isCursorVisible}`);
      duLog(`  - viewportHeight: ${viewportHeight}`);
      duLog(`  - isNearTopOfDocument: ${isNearTopOfDocument}`);
      duLog(`  - scrollTop: ${container.scrollTop}`);
      duLog(`  - domNodeRect:`, domNodeRect);
      
      duLog(`🍃 [NATIVE] Centering calculation:`);
      duLog(`  - absoluteTop: ${absoluteTop}`);
      duLog(`  - viewportHeight: ${viewportHeight}`);
      duLog(`  - idealCenterScrollTop: ${idealCenterScrollTop}`);
      duLog(`  - maxScrollTop: ${maxScrollTop}`);
      duLog(`  - bestEffortScrollTop: ${bestEffortScrollTop}`);
      duLog(`  - currentScrollTop: ${container.scrollTop}`);
      duLog(`  - canCenterFully: ${idealCenterScrollTop >= 0 && idealCenterScrollTop <= maxScrollTop}`);
      duLog(`  - scrollDistance: ${bestEffortScrollTop - container.scrollTop}`);
      duLog(`  - domNodeHeight: ${domNodeRect.height}`);
      duLog(`  - containerScrollHeight: ${container.scrollHeight}`);
      
      // Always use calculated centering for better accuracy
      // Only use special handling for extremely top cursors when already at scrollTop 0
      if (isVeryTopOfDocument && container.scrollTop === 0 && idealCenterScrollTop < 0) {
        // Cursor is at very top of document and we can't scroll up further
        // Scroll down a bit to give cursor some space
        const aggressiveScrollTop = Math.min(viewportHeight * 0.2, maxScrollTop);
        duLog(`🍃 [NATIVE] Cursor at very top, using aggressive scroll: 0 -> ${aggressiveScrollTop}`);
        
        const beforeScrollTop = container.scrollTop;
        container.scrollTo({
          top: aggressiveScrollTop,
          behavior: 'smooth'
        });
        
        setTimeout(() => {
          const afterScrollTop = container.scrollTop;
          duLog(`🍃 [NATIVE] Aggressive scroll result: ${beforeScrollTop} -> ${afterScrollTop}`);
        }, 100);
      } else {
        // Use calculated centering for all other cases
        const beforeScrollTop = container.scrollTop;
        const scrollDistance = Math.abs(bestEffortScrollTop - beforeScrollTop);
        
        // Only scroll if there's a meaningful distance to scroll
        if (scrollDistance > 5) {
          duLog(`[LOG-7] 🍃 [NATIVE] Executing calculated centering scroll: ${beforeScrollTop} -> ${bestEffortScrollTop} (distance: ${scrollDistance}px)`);
          container.scrollTo({
            top: bestEffortScrollTop,
            behavior: 'smooth'
          });
          
          // Verify and potentially adjust after scroll completes
          setTimeout(() => {
            const afterScrollTop = container.scrollTop;
            const actualScrollDistance = Math.abs(afterScrollTop - beforeScrollTop);
            const expectedScrollDistance = Math.abs(bestEffortScrollTop - beforeScrollTop);
            duLog(`🍃 [NATIVE] Calculated centering scroll verification:`);
            duLog(`  - beforeScrollTop: ${beforeScrollTop}`);
            duLog(`  - afterScrollTop: ${afterScrollTop}`);
            duLog(`  - expectedScrollTop: ${bestEffortScrollTop}`);
            duLog(`  - actualScrollDistance: ${actualScrollDistance}`);
            duLog(`  - expectedScrollDistance: ${expectedScrollDistance}`);
            
            // If scroll didn't reach target (within 20px tolerance), try one more time
            if (Math.abs(afterScrollTop - bestEffortScrollTop) > 20) {
              duLog(`🍃 [NATIVE] Scroll didn't reach target (off by ${Math.abs(afterScrollTop - bestEffortScrollTop)}px), adjusting...`);
              container.scrollTo({
                top: bestEffortScrollTop,
                behavior: 'smooth'
              });
            }
          }, 300);
        } else {
          // Cursor is already well-centered, use scrollIntoView as fallback for fine-tuning
          duLog(`[LOG-8] 🍃 [NATIVE] Cursor already centered (distance: ${scrollDistance}px), using scrollIntoView for fine-tuning`);
          domNode.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }
      }
      
      duLog(`[LOG-9] ✅ [NATIVE] Successfully used native scrollIntoView for cursor centering (${Date.now() - startTime}ms)`);
      return true;
    } else {
      duLog(`[LOG-10] ❌ [NATIVE] No leaf found for cursor ${cursorIndex}`);
      return false;
    }
  } catch (error) {
    duLog('[LOG-11] ❌ [NATIVE] Error in native centering:', error);
    return false;
  }
};

// Specialized method for top-of-page cursor centering
export const scrollToCursorAndCenterTop = (editor, cursorIndex, cursorLength = 0) => {
  const startTime = Date.now();
  duLog(`🚀 [TOP] Starting top-of-page cursor centering at ${new Date().toISOString()}`, {
    cursorIndex,
    cursorLength,
    editorExists: !!editor,
    containerExists: !!editor?.container
  });
  
  try {
    // Set the cursor position first
    editor.setSelection(cursorIndex, cursorLength);
    duLog(`📍 [TOP] Set selection to index ${cursorIndex}, length ${cursorLength}`);
    
    // Get bounds using multiple methods
    let bounds = editor.getBounds(cursorIndex);
    duLog(`📏 [TOP] Initial bounds for cursor ${cursorIndex}:`, bounds);
    
    // If bounds are invalid, try alternative methods
    if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
      duLog(`⚠️ [TOP] Invalid bounds, trying alternative methods for cursor ${cursorIndex}`);
      
      // Try to get the leaf and calculate bounds manually
      const leaf = editor.getLeaf(cursorIndex);
      if (leaf && leaf[0] && leaf[0].domNode) {
        let domNode = leaf[0].domNode;
        
        // Handle case where domNode is a Text node
        if (domNode.nodeType === Node.TEXT_NODE) {
          duLog(`📏 [TOP] Leaf is Text node, finding parent element`);
          domNode = domNode.parentElement;
          if (!domNode) {
            duLog(`❌ [TOP] No parent element found for Text node`);
            return false;
          }
          duLog(`📏 [TOP] Using parent element:`, domNode);
        }
        
        const rect = domNode.getBoundingClientRect();
        const containerRect = editor.container.getBoundingClientRect();
        bounds = {
          top: rect.top - containerRect.top + editor.container.scrollTop,
          left: rect.left - containerRect.left,
          height: rect.height,
          width: rect.width
        };
        duLog(`📏 [TOP] Manual bounds calculation:`, bounds);
      }
    }
    
    if (bounds && bounds.top > 0 && bounds.height > 0 && editor.container) {
      const container = editor.container;
      const containerRect = container.getBoundingClientRect();
      const viewportHeight = containerRect.height;
      const currentScrollTop = container.scrollTop;
      
      // Calculate best-effort centering for top-of-page cursors
      const idealCenterScrollTop = bounds.top - (viewportHeight / 2) + (bounds.height / 2);
      const maxScrollTop = container.scrollHeight - viewportHeight;
      const bestEffortScrollTop = Math.max(0, Math.min(idealCenterScrollTop, maxScrollTop));
      
      duLog(`🎯 [TOP] Top-of-page centering calculation:`, {
        cursorIndex,
        bounds,
        viewportHeight,
        currentScrollTop,
        idealCenterScrollTop,
        maxScrollTop,
        bestEffortScrollTop,
        canCenterFully: idealCenterScrollTop >= 0 && idealCenterScrollTop <= maxScrollTop,
        scrollDistance: bestEffortScrollTop - currentScrollTop
      });
      
      // Use smooth scrolling to the calculated position
      container.scrollTo({
        top: bestEffortScrollTop,
        behavior: 'smooth'
      });
      
      duLog(`✅ [TOP] Successfully centered top-of-page cursor (${Date.now() - startTime}ms)`);
      return true;
    } else {
      duLog(`❌ [TOP] No valid bounds found for cursor ${cursorIndex}`);
      return false;
    }
  } catch (error) {
    duLog('❌ [TOP] Error in top-of-page centering:', error);
    return false;
  }
};

// Scroll to cursor and position it 15 lines above the bottom of the viewport
export const scrollToCursor15LinesAboveBottom = (editor, cursorIndex, cursorLength = 0) => {
  const startTime = Date.now();
  duLog(`🚀 [15LINES] Starting 15-lines-above-bottom positioning at ${new Date().toISOString()}`, {
    cursorIndex,
    cursorLength,
    editorExists: !!editor,
    containerExists: !!editor?.container
  });

  try {
    // Set the cursor position first
    editor.setSelection(cursorIndex, cursorLength);
    duLog(`📍 [15LINES] Set selection to index ${cursorIndex}, length ${cursorLength}`);

    // Get bounds for the cursor
    let bounds = editor.getBounds(cursorIndex);
    duLog(`📏 [15LINES] Initial bounds for cursor ${cursorIndex}:`, bounds);

    // If bounds are invalid, try alternative methods
    if (!bounds || bounds.top === 0 || bounds.height === 0 || bounds.top === undefined) {
      duLog(`⚠️ [15LINES] Invalid bounds, trying alternative methods for cursor ${cursorIndex}`);
      const leaf = editor.getLeaf(cursorIndex);
      if (leaf && leaf[0] && leaf[0].domNode) {
        let domNode = leaf[0].domNode;
        if (domNode.nodeType === Node.TEXT_NODE) {
          domNode = domNode.parentElement;
          if (!domNode) {
            duLog(`❌ [15LINES] No parent element found for Text node`);
            return false;
          }
        }
        const rect = domNode.getBoundingClientRect();
        const containerRect = editor.container.getBoundingClientRect();
        bounds = {
          top: rect.top - containerRect.top + editor.container.scrollTop,
          left: rect.left - containerRect.left,
          height: rect.height,
          width: rect.width
        };
        duLog(`📏 [15LINES] Manual bounds calculation:`, bounds);
      }
    }

    if (bounds && bounds.top > 0 && bounds.height > 0 && editor.container) {
      const container = editor.container;
      const containerRect = container.getBoundingClientRect();
      const viewportHeight = containerRect.height;
      const currentScrollTop = container.scrollTop;

      // Get the line height - use the cursor's height as line height, or estimate from container
      const lineHeight = bounds.height || 20; // Default to 20px if height is 0
      duLog(`📏 [15LINES] Line height: ${lineHeight}px`);

      // Calculate target position: 15 lines above the bottom of viewport
      const linesFromBottom = 15;
      const targetDistanceFromBottom = lineHeight * linesFromBottom;
      const targetViewportPosition = viewportHeight - targetDistanceFromBottom;

      // Get absolute position of cursor in document
      const cursorAbsoluteTop = bounds.top + currentScrollTop;
      const cursorCenter = cursorAbsoluteTop + (bounds.height / 2);

      // Calculate scroll position needed to place cursor center at target viewport position
      // Formula: After scrolling, cursorCenter - newScrollTop = targetViewportPosition
      // Therefore: newScrollTop = cursorCenter - targetViewportPosition
      let idealScrollTop = cursorCenter - targetViewportPosition;

      const maxScrollTop = container.scrollHeight - viewportHeight;
      const bestEffortScrollTop = Math.max(0, Math.min(idealScrollTop, maxScrollTop));

      duLog(`🎯 [15LINES] Positioning calculation:`, {
        cursorIndex,
        bounds,
        viewportHeight,
        currentScrollTop,
        cursorAbsoluteTop,
        cursorCenter,
        lineHeight,
        linesFromBottom,
        targetDistanceFromBottom,
        targetViewportPosition,
        idealScrollTop,
        maxScrollTop,
        bestEffortScrollTop,
        scrollDistance: bestEffortScrollTop - currentScrollTop
      });

      // Use smooth scrolling to the calculated position
      container.scrollTo({
        top: bestEffortScrollTop,
        behavior: 'smooth'
      });

      duLog(`✅ [15LINES] Successfully positioned cursor 15 lines above bottom (${Date.now() - startTime}ms)`);
      return true;
    } else {
      duLog(`❌ [15LINES] No valid bounds found for cursor ${cursorIndex}`);
      return false;
    }
  } catch (error) {
    duLog('❌ [15LINES] Error in 15-lines-above-bottom positioning:', error);
    return false;
  }
};

// Gets the Section Bounds
export const getSectionBounds = (editor, sectionTitle) => {
    const lines = editor.getLines();
    let startIndex = 0;
    let endIndex = 0;
    for (const line of lines) {
      const lineText = line.domNode?.innerText.trim();
      if (lineText === sectionTitle) {
        startIndex = editor.getIndex(line);
        endIndex = startIndex + line.length();
        break;
      }
    }
    return [startIndex, endIndex];
  };

//Helps Add or update any of the sections
export const addOrUpdateSection = (sections, lineText, isHeader, setSections) => {
    const updatedSections = [...sections];
    const sectionIndex = updatedSections.findIndex((sec) => sec.title === lineText);
  
    if (isHeader) {
      if (sectionIndex !== -1) {
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          lastupdated: new Date().toISOString(),
        };
      } else {
        const newSection = {
          id: `section-${sections.length}`,
          title: lineText,
          isEditing: false,
          lastupdated: new Date().toISOString(),
        };
        updatedSections.push(newSection);
      }
    }
  
    setSections(updatedSections);
  };
  
//Helps Mark Section as edited
export const markSectionAsEditing = (sections, sectionId, setSections) => {
    const updatedSections = sections.map((section) =>
      section.id === sectionId
        ? { ...section, isEditing: true, lastupdated: new Date().toISOString() }
        : { ...section, isEditing: false }
    );
    setSections(updatedSections);
  };

//Helps Format the Date
export const formatDate = (date) => {
    const formattedDate = new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    return formattedDate
}

export const updateLastUpdated = async (tableName, name) => {
  const supabase = await getSupabase();
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ last_updated: new Date().toISOString() })
        .eq(`${prompts[0][0]}`, name);

      if (error) {
        console.error("Failed to update last_updated:", error);
      } 
      else {
        duLog("last_updated updated successfully.");
      }
    } catch (err) {
      console.error("Unexpected error updating last_updated:", err);
    }
}

//Function to fetch Names based on the ScheduleDate
export const fetchNames = async (scheduleDate, tableName, setNames) => {
  const supabase = await getSupabase();
  try {
    duLog("Fetching with date:", scheduleDate);
    duLog("Table name:", tableName);
    
    // Create base query fields
    let queryFields = `${colid}, ${prompts[0][0]}, date_, last_updated, time_, user_id, timezone`;
    
    // Only add type field if it exists
    const typeFieldIndex = getTypeFieldIndex();
    if (typeFieldIndex !== null && prompts[typeFieldIndex] && prompts[typeFieldIndex][0]) {
      queryFields += `, ${prompts[typeFieldIndex][0]}`;
    }
    
    duLog("Query fields:", queryFields);
    
    const { data, error } = await supabase
      .from(tableName)
      .select(queryFields)
      .eq("date_", scheduleDate);

    if (error) {
      console.error("Error fetching names:", error);
    } else {
      duLog("Raw data from DB:", data);
      const hydrated = await hydrateScheduleRowsTimezoneSource(
        supabase,
        data || [],
      );
      setNames(hydrated);
    }
  } catch (error) {
    console.error("Error fetching names:", error);
  }
};

export const fetchDocument = async (bucketName, filePath) => {
  const supabase = await getSupabase();
  try {
    let preflightTrustSize = false;
    if (isRestrictedJsonStorageBucket(bucketName)) {
      const pf = await preflightRestrictedStorageJsonSize(
        supabase,
        bucketName,
        filePath,
      );
      if (pf.status === "too_large") {
        console.warn(
          "fetchDocument: skipped download (preflight size):",
          filePath,
        );
        return {
          delta: { ops: [] },
          sections: [],
          lastUpdated: null,
          storageJsonTooLarge: true,
          storageJsonTooLargeMessage: pf.message,
          storageJsonOfferCreateNew: true,
        };
      }
      if (pf.status === "not_found") {
        duLog(`Document not found at path: ${filePath}`);
        return {
          delta: { ops: [] },
          sections: [],
          lastUpdated: null,
        };
      }
      if (pf.status === "ok") {
        preflightTrustSize = true;
      }
    }

    // Retrieve the file from Supabase storage
    const { data, error } = await supabase.storage
      .from(bucketName)
      .download(filePath);

    // Handle the case where the document does not exist
    if (error) {
      // Check for various "file not found" error conditions
      const isFileNotFound = 
        error.message?.includes("Object not found") ||
        error.message?.includes("not found") ||
        error.message?.includes("No such file") ||
        error.statusCode === 400 ||
        error.statusCode === 404 ||
        error.name === "StorageUnknownError" ||
        (error.message && error.message.toLowerCase().includes("bad request"));
      
      if (isFileNotFound) {
        duLog(`Document not found at path: ${filePath}`, error);
        // Return an empty document if the file doesn't exist
        return {
          delta: { ops: [] }, // Empty delta
          sections: [], // No sections
          lastUpdated: null, // No last updated timestamp
        };
      }
      // Throw other errors for further handling
      throw error;
    }

    if (
      !preflightTrustSize &&
      isRestrictedJsonStorageBucket(bucketName) &&
      isProbablyJsonStoragePath(filePath)
    ) {
      const sizeCheck = checkFrontendStorageJsonBlobSize(data);
      if (!sizeCheck.ok) {
        console.warn(
          "fetchDocument: JSON exceeds frontend size limit:",
          filePath,
          sizeCheck.message,
        );
        return {
          delta: { ops: [] },
          sections: [],
          lastUpdated: null,
          storageJsonTooLarge: true,
          storageJsonTooLargeMessage: sizeCheck.message,
          storageJsonOfferCreateNew: true,
        };
      }
    }

    // Read the file as text
    const fileContent = await data.text();

    // Parse the content to JSON
    return JSON.parse(fileContent);
  } catch (err) {
    // Check if this is a "file not found" error that wasn't caught above
    const isFileNotFound = 
      err.message?.includes("Object not found") ||
      err.message?.includes("not found") ||
      err.message?.includes("No such file") ||
      err.statusCode === 400 ||
      err.statusCode === 404 ||
      err.name === "StorageUnknownError" ||
      (err.message && err.message.toLowerCase().includes("bad request"));
    
    if (isFileNotFound) {
      duLog(`Document not found at path: ${filePath}`, err);
      // Return an empty document if the file doesn't exist
      return {
        delta: { ops: [] },
        sections: [],
        lastUpdated: null,
      };
    }
    
    // Log other unexpected errors
    console.error("Unexpected error fetching document:", err.message || err);
    // Return an empty document as a fallback
    return {
      delta: { ops: [] },
      sections: [],
      lastUpdated: null,
    };
  }
};

/**
 * Generate MedADocs file path with schema support
 * Path format: New/MedADocs/[schema]/year/date/filename.json
 * (schema is only included if not "public")
 * 
 * @param {string} year - The year (e.g., "2025")
 * @param {string} date - The scheduled date (e.g., "2025-11-12")
 * @param {string} filename - The filename (e.g., "Patient Name-123.json")
 * @param {string|null} userSchema - Optional schema name. If not provided, will fetch from current user
 * @returns {Promise<string>} The full file path
 */
export const generateMedADocsPath = async (year, date, filename, userSchema = null) => {
  let schema = userSchema;
  
  // If schema not provided, use the already-parsed active schema from supabaseClient
  if (!schema) {
    schema = supabaseSchema || "public";
  }
  
  // Build path with schema prefix if not public
  const schemaPrefix = schema && schema !== "public" ? `${schema}/` : "";
  return `New/MedADocs/${schemaPrefix}${year}/${date}/${filename}`;
};

/**
 * Generate MedADocs folder path with schema support (synchronous version)
 * Path format: New/MedADocs/[schema]/year/date/
 * (schema is only included if not "public")
 * 
 * @param {string} year - The year (e.g., "2025")
 * @param {string} date - The scheduled date (e.g., "2025-11-12")
 * @param {string} schema - Schema name (defaults to "public" if not provided)
 * @returns {string} The folder path
 */
export const generateMedADocsFolderPath = (year, date, schema = "public") => {
  const schemaPrefix = schema && schema !== "public" ? `${schema}/` : "";
  return `New/MedADocs/${schemaPrefix}${year}/${date}/`;
};

/**
 * Generate the Vita-Med Drive base storage path for a schema.
 * Path format: New/Vita-MedDrive/[schema]/
 */
export const generateVitaMedDriveBasePath = (schema = "public") => {
  const schemaPrefix = schema && schema !== "public" ? `${schema}/` : "";
  return `New/Vita-MedDrive/${schemaPrefix}`;
};

/**
 * Paths that may exist in documents.path for the same logical file (tenant schema segment vs public).
 * @see generateMedADocsPath — layout is New/MedADocs/[schema/]year/date/filename
 */
export function medADocsPathVariantsForDocumentsLookup(filePath) {
  if (!filePath || typeof filePath !== "string") return [];
  const seen = new Set();
  const out = [];
  const add = (p) => {
    const t = p.trim();
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  };
  add(filePath);

  const re =
    /^New\/MedADocs\/([^/]+)\/(\d{4})\/(\d{4}-\d{2}-\d{2})\/(.+)$/;
  const m = filePath.trim().match(re);
  if (!m) return out;
  const [, seg, year, date, rest] = m;

  if (!/^\d{4}$/.test(seg)) {
    add(`New/MedADocs/${year}/${date}/${rest}`);
  } else {
    const schema = supabaseSchema || "public";
    if (schema && schema !== "public") {
      add(`New/MedADocs/${schema}/${year}/${date}/${rest}`);
    }
  }
  return out;
}

/**
 * EHR / patient-id segment from a MedADocs storage basename for navbar titles (generateDocFilename).
 * Handles simple `Name-12345.json` and long `12:00 Name - 12345 NP.json` forms.
 */
export function parsePatientIdSegmentFromMedADocsJsonPath(filePath) {
  if (!filePath || typeof filePath !== "string") return "";
  const base = filePath.replace(/\\/g, "/").split("/").pop() || "";
  const noExt = base.replace(/\.json$/i, "");
  if (!noExt) return "";
  // Long navbar title file: "12:00 Name - 12345 NP" (generateDocFilename + .json)
  const longForm = noExt.match(
    /^\d{1,2}:\d{2}\s+.+?\s-\s(\S+)\s+\S+$/,
  );
  if (longForm) return longForm[1].trim();
  const li = noExt.lastIndexOf("-");
  if (li <= 0) return "";
  const seg = noExt.slice(li + 1).trim();
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      seg,
    )
  ) {
    return "";
  }
  return seg;
}

/**
 * MedADocs path using `{patientName}-{EHR|row.id}.json` — matches schedules cleanup,
 * many `documents.path` values, and process-form flows (distinct from generateDocFilename titles).
 */
export async function deriveSimpleScheduleMedADocsPath(row) {
  if (!row?.date_) return null;
  try {
    const nameField = prompts[0]?.[0];
    const patientName =
      nameField != null && row[nameField] != null && String(row[nameField]).trim() !== ""
        ? String(row[nameField])
        : row.name || "SOAP_note";
    const ehrIdx = prompts.findIndex((p) => p[0] === "EHR_ID");
    const patientIdForFile =
      ehrIdx >= 0 &&
      row[prompts[ehrIdx][0]] !== undefined &&
      row[prompts[ehrIdx][0]] !== null &&
      String(row[prompts[ehrIdx][0]]).trim() !== ""
        ? String(row[prompts[ehrIdx][0]]).trim()
        : row.id;
    const filename = `${patientName}-${patientIdForFile}.json`;
    const year = row.date_.split("-")[0];
    return await generateMedADocsPath(year, row.date_, filename);
  } catch (e) {
    duLog("deriveSimpleScheduleMedADocsPath", e);
    return null;
  }
}

/** Long title path (`generateDocFilename` + `.json`) — distinct from simple Name-EHR.json. */
export async function deriveLongScheduleMedADocsPath(row) {
  if (!row?.date_) return null;
  try {
    const title = generateDocFilename(row);
    const storageFileName = `${title}.json`;
    const year = row.date_.split("-")[0];
    return await generateMedADocsPath(year, row.date_, storageFileName);
  } catch (e) {
    duLog("deriveLongScheduleMedADocsPath", e);
    return null;
  }
}

/**
 * Walk ordered path candidates; return the first `documents` row (canonical `path` + `id`).
 */
export async function resolveDocumentsTableIdByPathFirstMatch(
  supabase,
  pathsInOrder,
) {
  if (!supabase || !Array.isArray(pathsInOrder)) {
    return { id: null, path: null, tried: [] };
  }
  const tried = [];
  const seen = new Set();
  for (const filePath of pathsInOrder) {
    if (!filePath || !String(filePath).includes("MedADocs")) continue;
    const key = String(filePath).trim();
    if (seen.has(key)) continue;
    seen.add(key);
    const variants = medADocsPathVariantsForDocumentsLookup(filePath);
    if (!variants.length) {
      tried.push({ filePath, variants: 0 });
      continue;
    }
    try {
      const { data: rows, error } = await supabase
        .from("documents")
        .select("id, path")
        .in("path", variants);
      if (error) {
        duLog("resolveDocumentsTableIdByPathFirstMatch", error.message);
        tried.push({ filePath, error: error.message });
        continue;
      }
      if (!rows?.length) {
        tried.push({ filePath, rows: 0, variants });
        continue;
      }
      const trimmed = filePath.trim();
      const exact = rows.find((r) => r.path === trimmed);
      const chosen = exact || rows[0];
      tried.push({
        filePath,
        chosenId: chosen.id,
        chosenPath: chosen.path,
      });
      if (chosen?.id) {
        console.info("[documents.path lookup] first match", {
          triedCount: tried.length,
          chosenId: chosen.id,
          chosenPath: chosen.path,
        });
        return { id: chosen.id, path: chosen.path, tried };
      }
    } catch (e) {
      tried.push({ filePath, exception: String(e) });
    }
  }
  console.info("[documents.path lookup] no match for candidates", { tried });
  return { id: null, path: null, tried };
}

/** Resolve documents.id for a MedADocs storage path (matches EditableDocumentPage lookup). */
export async function resolveDocumentsTableIdByPath(supabase, filePath) {
  if (!supabase || !filePath || !String(filePath).includes("MedADocs")) {
    return null;
  }
  const variants = medADocsPathVariantsForDocumentsLookup(filePath);
  if (!variants.length) return null;
  try {
    const { data: rows, error } = await supabase
      .from("documents")
      .select("id, path")
      .in("path", variants);
    if (error) {
      duLog("resolveDocumentsTableIdByPath:", error.message);
      return null;
    }
    if (!rows?.length) {
      console.info("[documents.path lookup] no row", { variants });
      return null;
    }
    const trimmed = filePath.trim();
    const exact = rows.find((r) => r.path === trimmed);
    const chosen = exact || rows[0];
    console.info("[documents.path lookup] match", {
      variantCount: variants.length,
      rowCount: rows.length,
      chosenId: chosen?.id,
      chosenPath: chosen?.path,
    });
    return chosen.id;
  } catch (e) {
    duLog("resolveDocumentsTableIdByPath:", e);
    console.warn("[documents.path lookup] exception", e);
    return null;
  }
}

